'use client';

import Image from 'next/image';
import { useState } from 'react';
import { UsersIcon, MonitorIcon, SparklesIcon, ChartLineIcon } from 'lucide-react';

const screenshots = [
  {
    id: 0,
    title: 'Creator Context Profile',
    description: 'Manage your creator profile and track your journey',
    icon: UsersIcon,
    iconGradient: 'from-purple-500 to-indigo-500',
    image: '/gifs/profile.gif',
    alt: 'Ignission Creator Profile Demo',
  },
  {
    id: 1,
    title: 'Video Content Analysis',
    description: 'Get intelligent analysis your video performance and audience engagement',
    icon: MonitorIcon,
    iconGradient: 'from-blue-500 to-cyan-500',
    image: '/gifs/videos.gif',
    alt: 'Ignission Video Content Analysis Demo',
  },
  {
    id: 2,
    title: 'Intelligent Content Generator',
    description: 'Intelligent content ideas tailored to your audience',
    icon: SparklesIcon,
    iconGradient: 'from-orange-500 to-pink-500',
    image: '/gifs/ideas.gif',
    alt: 'Ignission Intelligent Ideas Demo',
  },
  {
    id: 3,
    title: 'Analytics Dashboard',
    description: 'Track your growth with detailed performance analytics',
    icon: ChartLineIcon,
    iconGradient: 'from-green-500 to-emerald-500',
    image: '/gifs/dashboard.gif',
    alt: 'Ignission Dashboard Demo',
  },
];

export function ScreenshotsSection() {
  const [selectedScreenshot, setSelectedScreenshot] = useState<null | (typeof screenshots)[0]>(
    null
  );

  return (
    <section className="relative z-10 py-24 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            See{' '}
            <span className="bg-gradient-to-r from-orange-400 to-pink-500 text-transparent bg-clip-text">
              Ignission
            </span>{' '}
            in Action
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            See how Ignission guides you through the content creation process.
          </p>
        </div>

        {/* Screenshots Grid Section (for comparison) */}
        <section className="relative z-10 py-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {screenshots.map(screenshot => (
                <div
                  key={screenshot.id}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-4 flex flex-col items-center hover:bg-white/10 transition-all duration-300 h-full cursor-pointer"
                  onClick={() => setSelectedScreenshot(screenshot)}
                  tabIndex={0}
                  role="button"
                  aria-label={`View larger ${screenshot.title}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') setSelectedScreenshot(screenshot);
                  }}
                >
                  <div
                    className={`p-2 rounded-lg bg-gradient-to-br ${screenshot.iconGradient} mb-2`}
                  >
                    <screenshot.icon className="w-6 h-6 text-white" strokeWidth={2} />
                  </div>
                  <h3 className="text-base font-bold mb-1 text-center">{screenshot.title}</h3>
                  <p className="text-gray-300 text-sm text-center mb-2">{screenshot.description}</p>
                  <div className="flex-1 w-full" />
                  <div className="rounded-lg overflow-hidden mb-2 w-full mt-auto">
                    <Image
                      src={screenshot.image}
                      alt={screenshot.alt}
                      width={400}
                      height={300}
                      className="w-full h-auto object-contain"
                      unoptimized
                    />
                  </div>
                </div>
              ))}
            </div>
            {/* Modal for large GIF view */}
            {selectedScreenshot && (
              <div
                className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
                onClick={() => setSelectedScreenshot(null)}
                tabIndex={-1}
                aria-modal="true"
                role="dialog"
                onKeyDown={e => {
                  if (e.key === 'Escape') setSelectedScreenshot(null);
                }}
              >
                <div
                  className="relative bg-white/10 rounded-2xl p-4 max-w-4xl w-full flex flex-col items-center shadow-2xl border border-white/20"
                  onClick={e => e.stopPropagation()}
                >
                  <button
                    className="absolute top-2 right-2 text-white bg-black/40 rounded-full p-2 hover:bg-black/70 transition"
                    onClick={() => setSelectedScreenshot(null)}
                    aria-label="Close large view"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={2}
                      stroke="currentColor"
                      className="w-6 h-6"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div
                    className={`p-2 rounded-lg bg-gradient-to-br ${selectedScreenshot.iconGradient} mb-4`}
                  >
                    <selectedScreenshot.icon className="w-8 h-8 text-white" strokeWidth={2} />
                  </div>
                  <h3 className="text-xl font-bold mb-2 text-center">{selectedScreenshot.title}</h3>
                  <p className="text-gray-300 text-base text-center mb-4">
                    {selectedScreenshot.description}
                  </p>
                  <div className="rounded-lg overflow-hidden w-full flex justify-center">
                    <Image
                      src={selectedScreenshot.image}
                      alt={selectedScreenshot.alt}
                      width={1200}
                      height={900}
                      className="w-full max-w-4xl h-auto object-contain"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    </section>
  );
}
